/**
 * Create F&O Job Dialog
 *
 * Creates a new Job in F&O with JobId = the Role Master code (stable),
 * TitleId/Description = the human title, optional function/job family,
 * then auto-links the RM entry.
 */

import React, { useState, useEffect } from 'react';
import { Loader2, PlusCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import roleMasterService from '@/services/api/roleMasterService';
import { jobFamilySyncService } from '@/services/api/organizationService';

interface CreateFoJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryId: number;
  entryTitle: string;
  roleCode: string;
  sageTitleCode?: string;
  functionalArea?: string;
  gradeLevel?: string;
  onCreated: () => void;
}

interface CreateResult {
  job_created: boolean;
  title_created: boolean;
  linked: boolean;
  errors: Array<{ entity: string; key: string; error: string }>;
}

const CreateFoJobDialog: React.FC<CreateFoJobDialogProps> = ({
  open,
  onOpenChange,
  entryId,
  entryTitle,
  roleCode,
  sageTitleCode,
  functionalArea,
  gradeLevel,
  onCreated,
}) => {
  const [createTitle, setCreateTitle] = useState('');
  const [createJobFamily, setCreateJobFamily] = useState('');
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [result, setResult] = useState<CreateResult | null>(null);

  useEffect(() => {
    if (open) {
      const cleanTitle = entryTitle.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
      setCreateTitle(cleanTitle);
      setCreateJobFamily(functionalArea || '');
      setResult(null);
    }
  }, [open, entryTitle, functionalArea]);

  const handleCreateJob = async () => {
    const cleanTitle = createTitle.trim();
    if (!roleCode || !cleanTitle) return;
    setIsCreatingJob(true);
    setResult(null);
    try {
      const createResult = await jobFamilySyncService.createJob({
        job_id: roleCode,
        title_id: cleanTitle,
        description: cleanTitle,
        job_family_id: createJobFamily || undefined,
        sage_title_code: sageTitleCode || undefined,
        compensation_level: gradeLevel || undefined,
      });

      let linked = false;
      if (createResult.job_created) {
        try {
          await roleMasterService.linkJob(entryId, roleCode, cleanTitle, false);
          linked = true;
        } catch (e) {
          console.error('Auto-link failed:', e);
        }
      }

      const res: CreateResult = {
        job_created: createResult.job_created,
        title_created: createResult.title_created,
        linked,
        errors: createResult.errors || [],
      };
      setResult(res);

      // Only auto-close if fully successful (no errors)
      if (res.job_created && res.linked && res.errors.length === 0) {
        onOpenChange(false);
        onCreated();
      } else if (res.job_created && res.linked) {
        // Created + linked but with warnings — refresh parent but keep dialog open to show errors
        onCreated();
      }
    } catch (error: any) {
      setResult({
        job_created: false,
        title_created: false,
        linked: false,
        errors: [{ entity: 'general', key: '', error: error?.response?.data?.detail || error?.message || 'Creation failed' }],
      });
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleClose = () => {
    // If job was created (even with warnings), refresh parent
    if (result?.job_created) {
      onCreated();
    }
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-lg">Create New Job in F&O</h3>
          <p className="text-sm text-gray-500 mt-1">
            For role <span className="font-medium text-gray-900">"{entryTitle}"</span>
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Job ID (Role Master code)</label>
            <Input value={roleCode} disabled className="font-mono" />
            {!roleCode ? (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" /> entry has no role code
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">Stable Role Master code — becomes the F&O JobId and never changes</p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Title *</label>
            <Input
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              placeholder="e.g., Academic Operations Coordinator"
              disabled={!!result?.job_created}
            />
            <p className="text-xs text-gray-400 mt-1">
              Human name shown everywhere (F&O TitleId and Description). Renames later only change this Title — the Job ID stays the stable RM code.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Function (Job Family)</label>
            <Input
              value={createJobFamily}
              onChange={(e) => setCreateJobFamily(e.target.value)}
              placeholder="e.g., Academics, Finance, IT"
              disabled={!!result?.job_created}
            />
            <p className="text-xs text-gray-400 mt-1">Must match an existing F&O Job Family ID</p>
          </div>

          {/* Result display */}
          {result && (
            <div className="space-y-2">
              {result.job_created && (
                <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-2 rounded">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Job created{result.title_created ? ' (Title created)' : ''}{result.linked ? ' and linked to role' : ''}</span>
                </div>
              )}
              {!result.job_created && result.errors.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 p-2 rounded">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Job creation failed</span>
                </div>
              )}
              {result.errors.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-amber-700">
                    {result.job_created ? 'Warnings:' : 'Errors:'}
                  </div>
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-xs text-gray-600 bg-amber-50 p-2 rounded border border-amber-100">
                      <span className="font-medium">{err.entity}{err.key ? `: ${err.key}` : ''}</span>
                      <br />
                      {err.error.length > 200 ? err.error.slice(0, 200) + '...' : err.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            {result?.job_created ? 'Close' : 'Cancel'}
          </Button>
          {!result?.job_created && (
            <Button
              onClick={handleCreateJob}
              disabled={isCreatingJob || !roleCode || !createTitle.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isCreatingJob ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PlusCircle className="h-4 w-4 mr-1" />}
              Create Job
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFoJobDialog;
